import React, { useRef, useEffect, useState, useMemo } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  Chip,
  Tooltip,
  Box,
  Badge,
  Button
} from '@mui/material';
import {
  Search,
  Clear,
  Visibility,
  Archive,
  CheckCircle,
  Inventory,
  Assignment,
  Save,
  RadioButtonUnchecked,
  PauseCircle,
  Download,
  TaskAlt,
  Print,
  GetApp,
  DoneAll,
  FileDownload,
  FilterAlt
} from '@mui/icons-material';
import AutoFocusScanner, { AutoFocusScannerRef } from './ScannerComponent';
import BarcodeDisplay from './BarcodeDisplay';
import { BoxProp, BoxStatus, ShipmentStatus } from '../../../types/Shipment.types';

// Types
interface Box extends BoxProp {}

interface BoxesTableProps {
  boxes: BoxProp[];
  shipmentStatus: ShipmentStatus;
  boxSearchTerm: string;
  onBoxSearchChange: (value: string) => void;
  statusFilter: 'all' | BoxStatus;
  onStatusFilterChange: (filter: 'all' | BoxStatus) => void;
  selectedBox: BoxProp | null;
  onBoxSelect: (box: BoxProp) => void;
  onBoxDetailsOpen: (box: BoxProp) => void;
  onPrintBoxLabel: (box: BoxProp) => void;
  onUpdateBoxStatus: (boxId: string, status: BoxStatus) => void;
  onPrintAllBoxLabels: () => void;
  onPrintItemsList: () => void;
  onPrintAllItems: () => void;
  onExportToCSV: () => void;
  autoFocus?: boolean;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  totalCount?: number;
}

const BoxesTable: React.FC<BoxesTableProps> = ({
  boxes,
  shipmentStatus,
  boxSearchTerm,
  onBoxSearchChange,
  statusFilter,
  onStatusFilterChange,
  selectedBox,
  onBoxSelect,
  onBoxDetailsOpen,
  onPrintBoxLabel,
  onUpdateBoxStatus,
  onPrintAllBoxLabels,
  onPrintItemsList,
  onPrintAllItems,
  onExportToCSV,
  autoFocus = false,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  totalCount
}) => {
  const searchRef = useRef<AutoFocusScannerRef>(null);
  const [localSearchTerm, setLocalSearchTerm] = useState(boxSearchTerm);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  // Debounced search - update parent after 300ms of inactivity
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      onBoxSearchChange(localSearchTerm);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [localSearchTerm, onBoxSearchChange]);

  // Sync with external changes
  useEffect(() => {
    if (boxSearchTerm !== localSearchTerm) {
      setLocalSearchTerm(boxSearchTerm);
    }
  }, [boxSearchTerm]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'b') {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Auto-focus search when in reception mode
  useEffect(() => {
    if (autoFocus && shipmentStatus === ShipmentStatus.InReception) {
      const timer = setTimeout(() => {
        searchRef.current?.focus();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [autoFocus, shipmentStatus]);

  // Get box status configuration
  const getBoxStatusConfig = (status: BoxStatus) => {
    switch (status) {
      case BoxStatus.New:
        return {
          color: 'info' as const,
          icon: <RadioButtonUnchecked />,
          label: 'New',
          bgcolor: '#e3f2fd',
          textColor: '#1976d2'
        };
      case BoxStatus.Paused:
        return {
          color: 'warning' as const,
          icon: <PauseCircle />,
          label: 'Paused',
          bgcolor: '#fff3e0',
          textColor: '#f57c00'
        };
      case BoxStatus.Completed:
        return {
          color: 'success' as const,
          icon: <CheckCircle />,
          label: 'Completed',
          bgcolor: '#e8f5e8',
          textColor: '#2e7d32'
        };
      default:
        return {
          color: 'default' as const,
          icon: <Inventory />,
          label: String(status),
          bgcolor: '#f5f5f5',
          textColor: '#757575'
        };
    }
  };

  const getFilteredBoxes = () => {
    let filtered = boxes;

    if (boxSearchTerm.trim()) {
      const searchLower = boxSearchTerm.toLowerCase();
      filtered = boxes.filter(
        (box) =>
          box.refNumber.toLowerCase().includes(searchLower) ||
          box.barcode.toLowerCase().includes(searchLower) ||
          box.id.toLowerCase().includes(searchLower)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((box) => box.status === statusFilter);
    }

    filtered.sort((a, b) => {
      const statusOrder = [BoxStatus.New, BoxStatus.Paused, BoxStatus.Completed];
      return statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
    });

    return filtered;
  };

  const filteredBoxes = getFilteredBoxes();
  const totalScannedItems = boxes.reduce((sum, box) => sum + box.boxItems.length, 0);
  const completedBoxes = boxes.filter((box) => box.status === BoxStatus.Completed).length;
  const pausedBoxes = boxes.filter((box) => box.status === BoxStatus.Paused).length;
  const newBoxes = boxes.filter((box) => box.status === BoxStatus.New).length;

  const handleBoxScan = (searchTerm: string) => {
    const foundBox = boxes.find(
      (box) =>
        box.barcode === searchTerm ||
        box.refNumber === searchTerm ||
        box.id === searchTerm
    );

    if (foundBox) {
      onBoxSelect(foundBox);
      onBoxDetailsOpen(foundBox);
    }
  };

  const handleMarkAllComplete = async () => {
    if (window.confirm(`Mark all ${newBoxes + pausedBoxes} incomplete boxes as complete?`)) {
      const incompleteBoxes = boxes.filter(
        box => box.status !== BoxStatus.Completed
      );

      for (const box of incompleteBoxes) {
        await onUpdateBoxStatus(box.id, BoxStatus.Completed);
      }
    }
  };

  const handleQuickFilterIncomplete = () => {
    if (statusFilter === 'all') {
      // Show only incomplete boxes (New + Paused)
      onStatusFilterChange(BoxStatus.New);
    } else {
      // Reset to show all
      onStatusFilterChange('all');
    }
  };

  return (
    <Card>
      <CardHeader
        title="Boxes Overview"
        action={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Badge badgeContent={boxes.length} color="primary">
              <Inventory />
            </Badge>
            <Badge badgeContent={totalScannedItems} color="success">
              <Assignment />
            </Badge>

            {/* Bulk Operations */}
            {shipmentStatus === ShipmentStatus.InReception && (newBoxes + pausedBoxes) > 0 && (
              <Tooltip title="Mark all incomplete boxes as complete">
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<DoneAll />}
                  onClick={handleMarkAllComplete}
                >
                  Complete All ({newBoxes + pausedBoxes})
                </Button>
              </Tooltip>
            )}

            {/* <Tooltip title="Toggle: Show only incomplete boxes">
              <Button
                size="small"
                variant={statusFilter !== 'all' ? 'contained' : 'outlined'}
                startIcon={<FilterAlt />}
                onClick={handleQuickFilterIncomplete}
              >
                {statusFilter === 'all' ? 'Show Incomplete' : 'Show All'}
              </Button>
            </Tooltip> */}

            <Button
              size="small"
              variant="outlined"
              startIcon={<Print />}
              onClick={onPrintAllBoxLabels}
              disabled={boxes.length === 0}
            >
              Print All Labels
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Print />}
              onClick={onPrintItemsList}
              disabled={totalScannedItems === 0}
            >
              Print Box Items
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Print />}
              onClick={onPrintAllItems}
              disabled={totalScannedItems === 0}
            >
              Print All Items
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={onExportToCSV}
              disabled={boxes.length === 0}
            >
              Export CSV
            </Button>
          </Stack>
        }
      />
      <CardContent>
        {boxes.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2
              }}
            >
              <Inventory sx={{ fontSize: 40, color: 'grey.400' }} />
            </Box>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Boxes Generated
            </Typography>
            <Typography color="text.secondary">
              Accept the shipment to generate boxes automatically
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {/* Enhanced Search and Filter Bar */}
            <Stack direction="row" spacing={2} alignItems="center">
              <AutoFocusScanner
                ref={searchRef}
                fullWidth
                size="small"
                placeholder="Search boxes by ID, barcode, or reference number... (Ctrl+B to focus)"
                value={localSearchTerm}
                onChange={setLocalSearchTerm}
                onScan={handleBoxScan}
                autoFocus={autoFocus && shipmentStatus === ShipmentStatus.InReception}
                autoSelect={true}
                clearAfterScan={true}
                scannerIcon={<Search />}
                focusDelay={500}
                helperText="💡 Press Ctrl+B to focus this field, Enter to open box details directly"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Press Ctrl+B to focus">
                        <Box sx={{
                          bgcolor: 'grey.100',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          color: 'text.secondary'
                        }}>
                          Ctrl+B
                        </Box>
                      </Tooltip>
                    </InputAdornment>
                  )
                }}
              />
              <Autocomplete
                size="small"
                options={[
                  { value: 'all', label: 'All Status', count: boxes.length },
                  { value: BoxStatus.New, label: 'New', count: newBoxes },
                  { value: BoxStatus.Paused, label: 'Paused', count: pausedBoxes },
                  { value: BoxStatus.Completed, label: 'Completed', count: completedBoxes }
                ]}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : `${option.label} (${option.count})`
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Filter by status..." />
                )}
                value={
                  statusFilter === 'all'
                    ? { value: 'all', label: 'All Status', count: boxes.length }
                    : {
                        value: statusFilter,
                        label: BoxStatus[statusFilter],
                        count: boxes.filter(b => b.status === statusFilter).length
                      }
                }
                onChange={(_, value) => {
                  onStatusFilterChange(
                    typeof value === 'object' && value !== null && 'value' in value
                      ? (value.value as 'all' | BoxStatus)
                      : 'all'
                  );
                }}
                sx={{ minWidth: 200 }}
              />
            </Stack>

            {/* Boxes Table */}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Reference</strong></TableCell>
                    <TableCell><strong>Barcode</strong></TableCell>
                    <TableCell align="center"><strong>Items</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredBoxes
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((box) => {
                      const statusConfig = getBoxStatusConfig(box.status);
                      return (
                        <TableRow
                          key={box.id}
                          hover
                          sx={{
                            bgcolor: selectedBox?.id === box.id
                              ? 'primary.50'
                              : 'white',
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: selectedBox?.id === box.id
                                ? 'primary.100'
                                : 'grey.100'
                            }
                          }}
                          onClick={() => {
                            onBoxSelect(box);
                            onBoxDetailsOpen(box);
                          }}
                        >
                          <TableCell>
                            <Chip
                              icon={statusConfig.icon}
                              label={statusConfig.label}
                              size="small"
                              sx={{
                                bgcolor: statusConfig.bgcolor,
                                color: statusConfig.textColor,
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography variant="body2" fontWeight="medium">
                                {box.refNumber}
                              </Typography>
                              {selectedBox?.id === box.id && (
                                <Chip label="Opened" size="small" color="primary" />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {box.barcode || 'N/A'}
                              </Typography>
                            {/* <Stack direction="row" alignItems="center" spacing={1}>
                              <BarcodeDisplay
                                value={box.barcode}
                                width={100}
                                height={30}
                                showText={true}
                                style="table"
                              />
                            </Stack> */}
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={1} justifyContent="center">
                              <Chip
                                label={box.boxItems.length}
                                size="small"
                                color={box.boxItems.length > 0 ? 'primary' : 'default'}
                              />
                              <Typography variant="caption" color="text.secondary">
                                qty: {box.boxItems.reduce((sum, item) => sum + item.qty, 0)}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={1} justifyContent="center">
                              <Tooltip title="View Details">
                                <IconButton
                                  size="small"
                                  data-box-trigger="true"
                                  aria-label={`View details for box ${box.refNumber}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onBoxSelect(box);
                                    onBoxDetailsOpen(box);
                                  }}
                                >
                                  <Visibility />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Print Label">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onPrintBoxLabel(box);
                                  }}
                                  color="info"
                                >
                                  <Print /> {/* Changed from Archive to Print */}
                                </IconButton>
                              </Tooltip>
                              {shipmentStatus === ShipmentStatus.InReception &&
                                box.status !== BoxStatus.Completed && (
                                  <Tooltip title="Mark as Completed">
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onUpdateBoxStatus(box.id, BoxStatus.Completed);
                                      }}
                                    >
                                      <TaskAlt /> {/* Changed from CheckCircle to TaskAlt */}
                                    </IconButton>
                                  </Tooltip>
                                )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={totalCount || filteredBoxes.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => onPageChange(newPage)}
              onRowsPerPageChange={(e) => {
                onRowsPerPageChange(parseInt(e.target.value, 10));
                onPageChange(0);
              }}
            />
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default BoxesTable;